import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Building2, Users, Settings, LogOut, Plus, Pencil, Trash2, Power, Eye, ChevronRight, Package, Wrench, Target, FileText, Mail, User, Key, Database, ShieldCheck, Loader2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available modules with icons
const AVAILABLE_MODULES = [
  { id: 'lead_management', name: 'Lead Management', icon: Target, description: 'Track and manage sales leads' },
  { id: 'sales', name: 'Sales & Bookings', icon: FileText, description: 'Manage vehicle bookings' },
  { id: 'service', name: 'Service', icon: Wrench, description: 'Vehicle service management' },
  { id: 'spares', name: 'Spares & Inventory', icon: Package, description: 'Spare parts inventory' },
];

function SuperAdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('companies');
  const [companies, setCompanies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAddCompany, setShowAddCompany] = React.useState(false);
  const [editingCompany, setEditingCompany] = React.useState(null);
  const [selectedCompany, setSelectedCompany] = React.useState(null);
  const [companyUsers, setCompanyUsers] = React.useState([]);
  
  // Edit User State
  const [editingUser, setEditingUser] = React.useState(null);
  const [editUserData, setEditUserData] = React.useState({
    name: '',
    email: '',
    phone: '',
    new_password: ''
  });
  const [savingUser, setSavingUser] = React.useState(false);
  
  // Add User State
  const [showAddUser, setShowAddUser] = React.useState(false);
  const [newUserData, setNewUserData] = React.useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role_id: ''
  });
  const [companyRoles, setCompanyRoles] = React.useState([]);
  const [addingUser, setAddingUser] = React.useState(false);

  // Data Health (orphan audit + backfill) state
  const [showDataHealth, setShowDataHealth] = React.useState(false);
  const [dhLoading, setDhLoading] = React.useState(false);
  const [dhAudit, setDhAudit] = React.useState(null);   // { total_orphans, collections }
  const [dhTargetCode, setDhTargetCode] = React.useState('');
  const [dhRunning, setDhRunning] = React.useState(false);
  const [dhReport, setDhReport] = React.useState(null);
  
  const [newCompany, setNewCompany] = React.useState({
    name: '',
    code: '',
    city: '',
    phone: '',
    email: '',
    gst_no: '',
    enabled_modules: ['lead_management', 'sales']  // Default modules
  });

  const token = localStorage.getItem('access_token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  React.useEffect(() => {
    fetchCompanies();
  }, []);

  // -------------------- Data Health helpers --------------------
  const openDataHealth = async () => {
    setShowDataHealth(true);
    setDhAudit(null);
    setDhReport(null);
    setDhLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/maintenance/tenant-audit`, authHeader);
      setDhAudit(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load tenant audit');
    } finally {
      setDhLoading(false);
    }
  };

  const refreshAudit = async () => {
    setDhLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/maintenance/tenant-audit`, authHeader);
      setDhAudit(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load tenant audit');
    } finally {
      setDhLoading(false);
    }
  };

  const runBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm(
      `Backfill ${dhAudit?.total_orphans ?? 0} orphan rows across all multi-tenant collections?\n\n` +
      `Target: ${dhTargetCode || '(only company)'}\n\nThis is idempotent and safe to re-run.`
    )) return;
    setDhRunning(true);
    setDhReport(null);
    try {
      const body = {};
      if (dhTargetCode) body.target_company_code = dhTargetCode;
      if (dryRun) body.dry_run = true;
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/maintenance/backfill-orphan-tenants`,
        body, authHeader
      );
      setDhReport(data);
      if (!dryRun) {
        toast.success(`Backfill complete — ${data?.totals?.backfilled ?? 0} rows updated`);
        await refreshAudit();
      } else {
        toast.info(`Dry-run complete — would touch ${data?.totals?.backfilled ?? 0} rows`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    } finally {
      setDhRunning(false);
    }
  };
  // ----------------------------------------------------------------

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies`, authHeader);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        onLogout();
      } else {
        toast.error('Failed to fetch companies');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name || !newCompany.code) {
      toast.error('Name and Code are required');
      return;
    }
    
    // Validate code format
    if (!/^[a-z0-9_]+$/.test(newCompany.code)) {
      toast.error('Code must contain only lowercase letters, numbers, and underscores');
      return;
    }
    
    // Validate at least one module selected
    if (!newCompany.enabled_modules || newCompany.enabled_modules.length === 0) {
      toast.error('Please select at least one module');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/companies`, newCompany, authHeader);
      toast.success('Company created successfully');
      setShowAddCompany(false);
      setNewCompany({ name: '', code: '', city: '', phone: '', email: '', gst_no: '', enabled_modules: ['lead_management', 'sales'] });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleUpdateCompanyModules = async (companyId, modules) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/companies/${companyId}/modules`,
        { enabled_modules: modules },
        authHeader
      );
      toast.success('Company modules updated');
      fetchCompanies();
      if (selectedCompany?.id === companyId) {
        setSelectedCompany(prev => ({ ...prev, enabled_modules: modules }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update modules');
    }
  };

  const toggleModule = (moduleId) => {
    setNewCompany(prev => {
      const modules = prev.enabled_modules || [];
      if (modules.includes(moduleId)) {
        return { ...prev, enabled_modules: modules.filter(m => m !== moduleId) };
      } else {
        return { ...prev, enabled_modules: [...modules, moduleId] };
      }
    });
  };

  const toggleEditModule = (moduleId) => {
    setEditingCompany(prev => {
      if (!prev) return prev;
      const modules = prev.enabled_modules || [];
      if (modules.includes(moduleId)) {
        return { ...prev, enabled_modules: modules.filter(m => m !== moduleId) };
      } else {
        return { ...prev, enabled_modules: [...modules, moduleId] };
      }
    });
  };

  const handleToggleCompanyStatus = async (companyId) => {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/companies/${companyId}/toggle-status`, 
        {}, 
        authHeader
      );
      toast.success(response.data.message);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Are you sure you want to DELETE "${company.name}" and ALL its data? This cannot be undone!`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/companies/${company.id}?confirm=true`, authHeader);
      toast.success('Company deleted successfully');
      fetchCompanies();
      if (selectedCompany?.id === company.id) {
        setSelectedCompany(null);
        setCompanyUsers([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const handleViewCompanyUsers = async (company) => {
    setSelectedCompany(company);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies/${company.id}/users`, authHeader);
      setCompanyUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch company users');
      setCompanyUsers([]);
    }
  };

  // Edit User Functions
  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUserData({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      phone: userToEdit.phone || '',
      new_password: ''
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      setSavingUser(true);
      
      // Prepare update data
      const updateData = {
        name: editUserData.name,
        email: editUserData.email,
        phone: editUserData.phone
      };
      
      // Only include password if provided
      if (editUserData.new_password && editUserData.new_password.trim()) {
        updateData.password = editUserData.new_password;
      }

      await axios.put(
        `${BACKEND_URL}/api/companies/${selectedCompany.id}/users/${editingUser.id}`,
        updateData,
        authHeader
      );

      toast.success('User updated successfully!');
      setEditingUser(null);
      
      // Refresh users list
      handleViewCompanyUsers(selectedCompany);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  // Add User Functions
  const handleOpenAddUser = async () => {
    if (!selectedCompany) {
      toast.error('Please select a company first');
      return;
    }
    
    // Fetch roles for the selected company
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies/${selectedCompany.id}/roles`, authHeader);
      setCompanyRoles(response.data);
      // Set default role to Company Admin if exists
      const adminRole = response.data.find(r => r.name === 'Company Admin');
      setNewUserData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role_id: adminRole?.id || (response.data[0]?.id || '')
      });
      setShowAddUser(true);
    } catch (error) {
      toast.error('Failed to fetch roles');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error('Name, email and password are required');
      return;
    }
    
    if (!newUserData.role_id) {
      toast.error('Please select a role');
      return;
    }

    try {
      setAddingUser(true);
      await axios.post(
        `${BACKEND_URL}/api/companies/${selectedCompany.id}/users`,
        newUserData,
        authHeader
      );
      
      toast.success('User created successfully!');
      setShowAddUser(false);
      setNewUserData({ name: '', email: '', password: '', phone: '', role_id: '' });
      
      // Refresh users list
      handleViewCompanyUsers(selectedCompany);
    } catch (error) {
      console.error('Failed to add user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFE5D9 0%, #F4ACB7 100%)' }}>
                <span className="text-xl">🚗</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">AutoBook Pro</h1>
                <p className="text-xs text-gray-500">Super Admin Portal</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                <p className="text-xs text-[#F4ACB7]">{user?.role_name || 'Super Admin'}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{companies.length}</p>
                <p className="text-sm text-gray-500">Total Companies</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Power className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{companies.filter(c => c.is_active).length}</p>
                <p className="text-sm text-gray-500">Active Companies</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{companyUsers.length}</p>
                <p className="text-sm text-gray-500">{selectedCompany ? `Users in ${selectedCompany.name}` : 'Select a Company'}</p>
              </div>
            </div>
          </div>

          {/* Migration Tool Card */}
          <div 
            onClick={() => navigate('/admin/migration')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-all"
          >
            <div className="flex items-center gap-4 text-white">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Database className="w-6 h-6" />
              </div>
              <div>
                <p className="text-lg font-bold">Migration Tool</p>
                <p className="text-sm text-white/80">Link existing databases</p>
              </div>
            </div>
          </div>

          {/* Subscription Plans Card */}
          <div 
            onClick={() => navigate('/admin/subscription-plans')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-all"
            data-testid="admin-subscription-card"
          >
            <div className="flex items-center gap-4 text-white">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-bold">Subscription Plans</p>
                <p className="text-sm text-white/80">Manage plans & billing</p>
              </div>
            </div>
          </div>

          {/* Data Health Card */}
          <div
            onClick={openDataHealth}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-all"
            data-testid="admin-data-health-card"
          >
            <div className="flex items-center gap-4 text-white">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="text-lg font-bold">Data Health</p>
                <p className="text-sm text-white/80">Audit & backfill orphan rows</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Companies List */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Companies</h2>
              <button
                onClick={() => setShowAddCompany(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg hover:bg-[#e89aa5] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Company
              </button>
            </div>
            
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading companies...</div>
            ) : companies.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No companies yet. Create your first company to get started.</p>
              </div>
            ) : (
              <div className="divide-y">
                {companies.map(company => (
                  <div
                    key={company.id}
                    className={`p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition-colors ${selectedCompany?.id === company.id ? 'bg-blue-50' : ''}`}
                    onClick={() => handleViewCompanyUsers(company)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${company.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Building2 className={`w-5 h-5 ${company.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{company.name}</p>
                        <p className="text-sm text-gray-500">Code: {company.code} {company.city && `• ${company.city}`}</p>
                        {/* Show enabled modules */}
                        <div className="flex gap-1 mt-1">
                          {(company.enabled_modules || ['lead_management', 'sales']).map(moduleId => {
                            const module = AVAILABLE_MODULES.find(m => m.id === moduleId);
                            if (!module) return null;
                            const Icon = module.icon;
                            return (
                              <span key={moduleId} title={module.name} className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded text-gray-600">
                                <Icon className="w-3 h-3" />
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${company.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingCompany(company); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit Modules"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleCompanyStatus(company.id); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title={company.is_active ? 'Disable' : 'Enable'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteCompany(company); }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Company Users Panel */}
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                {selectedCompany ? `${selectedCompany.name} Users` : 'Company Users'}
              </h2>
              {selectedCompany && companyUsers.length > 0 && (
                <button
                  onClick={handleOpenAddUser}
                  className="px-3 py-1.5 bg-[#F4ACB7] text-white rounded-lg text-sm hover:bg-[#e89aa5] flex items-center gap-1"
                  data-testid="add-user-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add User
                </button>
              )}
            </div>
            
            {!selectedCompany ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Select a company to view its users</p>
              </div>
            ) : companyUsers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No users in this company yet</p>
                <button 
                  onClick={handleOpenAddUser}
                  className="mt-4 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg text-sm hover:bg-[#e89aa5]"
                  data-testid="add-first-user-btn"
                >
                  Add First User
                </button>
              </div>
            ) : (
              <div className="divide-y max-h-96 overflow-y-auto">
                {companyUsers.map(u => (
                  <div key={u.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                        {u.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{u.name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.role_name || 'User'}
                      </span>
                      <button
                        onClick={() => handleEditUser(u)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Edit User"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Company Modal */}
      {showAddCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h3 className="text-xl font-semibold text-gray-800">Add New Company</h3>
              <p className="text-sm text-gray-500 mt-1">Create a new company/tenant with its own database</p>
            </div>
            
            <form onSubmit={handleAddCompany} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany(prev => ({...prev, name: e.target.value}))}
                    placeholder="ABC Motors Pvt Ltd"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Code *</label>
                  <input
                    type="text"
                    value={newCompany.code}
                    onChange={(e) => setNewCompany(prev => ({...prev, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')}))}
                    placeholder="abc_motors"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Lowercase, numbers, underscores only</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={newCompany.city}
                    onChange={(e) => setNewCompany(prev => ({...prev, city: e.target.value}))}
                    placeholder="Mumbai"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newCompany.phone}
                    onChange={(e) => setNewCompany(prev => ({...prev, phone: e.target.value}))}
                    placeholder="9876543210"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newCompany.email}
                    onChange={(e) => setNewCompany(prev => ({...prev, email: e.target.value}))}
                    placeholder="contact@company.com"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    value={newCompany.gst_no}
                    onChange={(e) => setNewCompany(prev => ({...prev, gst_no: e.target.value.toUpperCase()}))}
                    placeholder="22AAAAA0000A1Z5"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  />
                </div>
                
                {/* Module Selection */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enable Modules *</label>
                  <p className="text-xs text-gray-500 mb-3">Select which modules this company can access</p>
                  <div className="grid grid-cols-2 gap-3">
                    {AVAILABLE_MODULES.map(module => {
                      const Icon = module.icon;
                      const isSelected = newCompany.enabled_modules?.includes(module.id);
                      return (
                        <button
                          key={module.id}
                          type="button"
                          onClick={() => toggleModule(module.id)}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            isSelected 
                              ? 'border-[#F4ACB7] bg-[#F4ACB7]/10' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isSelected ? 'bg-[#F4ACB7] text-white' : 'bg-gray-100 text-gray-500'
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div>
                              <p className={`font-medium text-sm ${isSelected ? 'text-[#F4ACB7]' : 'text-gray-700'}`}>
                                {module.name}
                              </p>
                              <p className="text-xs text-gray-500">{module.description}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddCompany(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg hover:bg-[#e89aa5]"
                >
                  Create Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modules Modal */}
      {editingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">Edit Company Modules</h3>
              <p className="text-sm text-gray-500 mt-1">{editingCompany.name}</p>
            </div>
            
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Enabled Modules</label>
              <div className="space-y-3">
                {AVAILABLE_MODULES.map(module => {
                  const Icon = module.icon;
                  const isSelected = editingCompany.enabled_modules?.includes(module.id);
                  return (
                    <button
                      key={module.id}
                      type="button"
                      onClick={() => toggleEditModule(module.id)}
                      className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-[#F4ACB7] bg-[#F4ACB7]/10' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-[#F4ACB7] text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${isSelected ? 'text-[#F4ACB7]' : 'text-gray-700'}`}>
                            {module.name}
                          </p>
                          <p className="text-xs text-gray-500">{module.description}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-[#F4ACB7] bg-[#F4ACB7]' : 'border-gray-300'
                        }`}>
                          {isSelected && <span className="text-white text-xs">✓</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setEditingCompany(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleUpdateCompanyModules(editingCompany.id, editingCompany.enabled_modules);
                    setEditingCompany(null);
                  }}
                  className="flex-1 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg hover:bg-[#e89aa5]"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">Edit User</h3>
              <p className="text-sm text-gray-500 mt-1">
                Update user details for {selectedCompany?.name}
              </p>
            </div>
            
            <form onSubmit={handleSaveUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Display Name
                </label>
                <input
                  type="text"
                  value={editUserData.name}
                  onChange={(e) => setEditUserData(prev => ({...prev, name: e.target.value}))}
                  placeholder="John Doe"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Login Email (Username) *
                </label>
                <input
                  type="email"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData(prev => ({...prev, email: e.target.value}))}
                  placeholder="user@company.com"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Changing email will change the login ID
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={editUserData.phone}
                  onChange={(e) => setEditUserData(prev => ({...prev, phone: e.target.value}))}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Key className="w-4 h-4 inline mr-1" />
                  New Password (Optional)
                </label>
                <input
                  type="password"
                  value={editUserData.new_password}
                  onChange={(e) => setEditUserData(prev => ({...prev, new_password: e.target.value}))}
                  placeholder="Leave blank to keep current password"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only fill this if you want to change the password
                </p>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <span className="text-sm text-blue-700">
                  <strong>Current Role:</strong> {editingUser.role_name || 'User'}
                </span>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingUser}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingUser ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Add New User</h3>
                <p className="text-sm text-gray-500">Create a user for {selectedCompany.name}</p>
              </div>
              <button
                onClick={() => setShowAddUser(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddUser} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={newUserData.name}
                  onChange={(e) => setNewUserData(prev => ({...prev, name: e.target.value}))}
                  placeholder="Enter full name"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={newUserData.email}
                  onChange={(e) => setNewUserData(prev => ({...prev, email: e.target.value}))}
                  placeholder="Enter email address"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Key className="w-4 h-4 inline mr-1" />
                  Password *
                </label>
                <input
                  type="password"
                  required
                  value={newUserData.password}
                  onChange={(e) => setNewUserData(prev => ({...prev, password: e.target.value}))}
                  placeholder="Enter password"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={newUserData.phone}
                  onChange={(e) => setNewUserData(prev => ({...prev, phone: e.target.value}))}
                  placeholder="Enter phone number"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  required
                  value={newUserData.role_id}
                  onChange={(e) => setNewUserData(prev => ({...prev, role_id: e.target.value}))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#F4ACB7]/50 focus:border-[#F4ACB7]"
                  data-testid="new-user-role"
                >
                  <option value="">Select a role</option>
                  {companyRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingUser}
                  className="flex-1 px-4 py-2 bg-[#F4ACB7] text-white rounded-lg hover:bg-[#e89aa5] disabled:opacity-50"
                  data-testid="submit-new-user"
                >
                  {addingUser ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Data Health Modal */}
      {showDataHealth && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="data-health-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Data Health</h2>
                  <p className="text-[12px] text-white/80">Audit + backfill orphan rows across all multi-tenant collections</p>
                </div>
              </div>
              <button onClick={() => setShowDataHealth(false)} className="p-2 hover:bg-white/15 rounded-lg" data-testid="data-health-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Audit summary */}
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[14px] font-semibold text-slate-700">Tenant Audit (read-only)</h3>
                  <button
                    onClick={refreshAudit}
                    disabled={dhLoading}
                    className="text-[12px] text-emerald-600 hover:underline disabled:opacity-50"
                    data-testid="data-health-refresh"
                  >
                    {dhLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
                {dhLoading && !dhAudit && (
                  <div className="flex items-center gap-2 text-slate-500 text-[13px]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                )}
                {dhAudit && (
                  <>
                    <div className={`flex items-center gap-2 mb-3 text-[14px] font-medium ${dhAudit.total_orphans === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {dhAudit.total_orphans === 0 ? (
                        <><CheckCircle2 className="w-4 h-4" /> 0 orphan rows — DB is clean</>
                      ) : (
                        <><AlertTriangle className="w-4 h-4" /> {dhAudit.total_orphans} total orphan rows found</>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Object.entries(dhAudit.collections || {}).map(([name, count]) => (
                        <div
                          key={name}
                          className={`px-3 py-2 rounded-lg text-[12px] flex items-center justify-between ${count > 0 ? 'bg-amber-50 text-amber-800' : 'bg-slate-50 text-slate-600'}`}
                          data-testid={`data-health-coll-${name}`}
                        >
                          <span className="font-mono">{name}</span>
                          <span className="font-semibold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Backfill controls */}
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/30">
                <h3 className="text-[14px] font-semibold text-slate-700 mb-3">Backfill orphan rows</h3>
                <p className="text-[12px] text-slate-500 mb-3">
                  Tags orphan rows with the chosen tenant. Idempotent — safe to run multiple times.
                  If only one company exists, target is auto-resolved.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-[11.5px] text-slate-500 mb-1">Target Company Code (optional)</label>
                    <select
                      value={dhTargetCode}
                      onChange={(e) => setDhTargetCode(e.target.value)}
                      data-testid="data-health-target-select"
                      className="px-3 py-2 rounded-lg border border-slate-200 text-[13px] min-w-[180px] focus:outline-none focus:border-emerald-400"
                    >
                      <option value="">(auto — only if 1 company)</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => runBackfill(true)}
                    disabled={dhRunning || (dhAudit?.total_orphans === 0)}
                    data-testid="data-health-dry-run"
                    className="px-4 py-2 rounded-lg border border-slate-300 text-[13px] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {dhRunning ? 'Running…' : 'Dry-run preview'}
                  </button>
                  <button
                    onClick={() => runBackfill(false)}
                    disabled={dhRunning || (dhAudit?.total_orphans === 0)}
                    data-testid="data-health-run"
                    className="px-5 py-2 rounded-lg text-white text-[13px] font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
                  >
                    {dhRunning ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Run Backfill'}
                  </button>
                </div>
              </div>

              {/* Report */}
              {dhReport && (
                <div className="rounded-xl border border-slate-200 p-4" data-testid="data-health-report">
                  <h3 className="text-[14px] font-semibold text-slate-700 mb-2">
                    Last run · {dhReport.dry_run ? 'Dry-run' : 'Live'} · target {dhReport.target_company?.code || '—'}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {Object.entries(dhReport.totals || {}).map(([k, v]) => (
                      <div key={k} className="px-3 py-2 rounded-lg bg-emerald-50 text-emerald-800 text-[12px] flex items-center justify-between">
                        <span>{k.replaceAll('_', ' ')}</span>
                        <span className="font-semibold">{v}</span>
                      </div>
                    ))}
                  </div>
                  <details className="text-[12px] text-slate-600">
                    <summary className="cursor-pointer hover:text-slate-800">Per-collection detail</summary>
                    <pre className="mt-2 p-3 bg-slate-900 text-emerald-200 rounded-lg overflow-x-auto text-[11px]">
{JSON.stringify(dhReport.collections, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t bg-slate-50 rounded-b-2xl flex justify-end">
              <button
                onClick={() => setShowDataHealth(false)}
                className="px-4 py-2 text-[13px] rounded-lg hover:bg-white border border-slate-200"
                data-testid="data-health-done"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
